import { memoize, range } from 'lodash';
import { DAYS, HOURS, MINUTES } from 'src/lib/player/constants';

const K = 1000;
const lineStridesX = [
  0.1,
  0.2,
  0.5,
  1,
  2,
  5,
  10,
  20,
  50,
  100,
  200,
  500,
  1 * K,
  2 * K,
  5 * K,
  10 * K,
  20 * K,
  50 * K,
  100 * K,
  200 * K,
  500 * K,
  1000 * K,
  2000 * K,
  5000 * K,
  10000 * K,
];
const lineStridesY = [0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
const lineStridesTime = [
  1 * MINUTES,
  5 * MINUTES,
  10 * MINUTES,
  15 * MINUTES,
  30 * MINUTES,
  1 * HOURS,
  3 * HOURS,
  6 * HOURS,
  12 * HOURS,
  1 * DAYS,
  2 * DAYS,
  5 * DAYS,
  7 * DAYS,
  30 * DAYS,
  365 * DAYS,
];

/**
 * Finds grid line positions for distance axis visualization.
 *
 * @param minX - The minimum distance value
 * @param maxX - The maximum distance value
 * @param count - The desired number of grid lines (default: 10)
 * @returns An array of grid line positions filtered to be within the valid range (greater than minX and less than maxX)
 *
 * @remarks
 * This function is memoized for performance optimization. It uses predefined distance strides
 * to determine appropriate spacing between grid lines based on the range.
 *
 * @example
 * ```typescript
 * const gridLines = findGridLinesDistance(0, 5000, 10);
 * // Returns array of evenly-spaced distance values
 * ```
 */
export const findGridLinesDistance = memoize(
  function findGridLinesDistance(minX: number, maxX: number, count = 10): Array<number> {
    return findGridLines(minX, maxX, count + 1, lineStridesX).filter((a) => a > minX && a < maxX);
  },
  (...args) => JSON.stringify(args),
);
/**
 * Finds evenly-spaced elevation gridline values within a given range.
 * @param minY - The minimum elevation value (lower bound)
 * @param maxY - The maximum elevation value (upper bound)
 * @param count - The desired number of gridlines to generate (default: 5)
 * @returns An array of elevation values suitable for rendering gridlines, filtered to fall strictly within the min/max range
 * @memoized Results are cached based on stringified arguments for performance
 */
export const findGridLinesElevation = memoize(
  function findGridLinesElevation(minY: number, maxY: number, count = 5): Array<number> {
    return findGridLines(minY, maxY, count, lineStridesY).filter((a) => a > minY && a < maxY);
  },
  (...args) => JSON.stringify(args),
);
/**
 * Finds time-based grid line positions within a specified range.
 * @param minX - The minimum time value (in the range).
 * @param maxX - The maximum time value (in the range).
 * @param count - The desired number of grid lines to generate. Defaults to 10.
 * @returns An array of time values representing grid line positions, filtered to exclude boundaries.
 * @memoized Results are cached based on the stringified arguments.
 */
export const findGridLinesTime = memoize(
  function findGridLinesTime(minX: number, maxX: number, count = 10): Array<number> {
    return findGridLines(minX, maxX, count, lineStridesTime).filter((a) => a > minX && a < maxX);
  },
  (...args) => JSON.stringify(args),
);
/**
 * Finds the grid lines for a given range and desired count.
 *
 * Determines an appropriate stride value based on the range and desired number of lines,
 * then generates grid line positions from the minimum to maximum values.
 *
 * @param min - The minimum value of the range
 * @param max - The maximum value of the range
 * @param count - The desired number of grid lines
 * @param lineStrides - An array of possible stride values, in ascending order
 * @returns An array of grid line positions, spaced by the selected stride
 */
export function findGridLines(
  min: number,
  max: number,
  count: number,
  lineStrides: Array<number>,
): Array<number> {
  let stride = lineStrides[0];
  for (const _stride of lineStrides) {
    if ((max - min) / _stride < count) {
      stride = _stride;
      break;
    }
  }
  const gridMin = Math.floor(min / stride) * stride;
  const gridMax = Math.ceil(max / stride) * stride;
  return range(gridMin, gridMax + 1, stride);
}
